import { PolicyPage } from './PolicyPage.js';
import type { CurrencyCode } from '../hooks/useCurrency.js';

/**
 * Terms of service (`docs/landing/terms-of-service.md`), published 2026-08-18.
 *
 * ⚠️ **`counselCleared={false}`, and here it matters more than on the privacy policy.** A ToS
 * carries limitation of liability, warranty disclaimers, indemnities and governing law — commercial
 * and legal positions that cannot be inferred from a codebase. This page states what the product
 * **does** and what we **commit to operationally**, both knowable, and marks the rest as counsel's.
 *
 * ⚠️ **§8 says out loud that the liability terms are not drafted.** Writing plausible ones would be
 * worse than leaving them open, because a reader cannot tell an invented clause from a considered
 * one — and this repo's standing rule is that an unstated limit is the dangerous kind.
 */
export interface TermsOfServiceProps {
  ccyCode: CurrencyCode;
  regionLabel: string;
  onCycleCurrency: () => void;
  onGoHome: () => void;
  onGoDocs: () => void;
  onGoConnectorsDocs: () => void;
  onGoPricing: () => void;
  onOpenAudit: () => void;
  onOpenBooking: () => void;
  onToggleHelp: () => void;
  onGoLegal: () => void;
}

export function TermsOfService(props: TermsOfServiceProps) {
  const { onGoLegal, ...shell } = props;
  return (
    <PolicyPage
      {...shell}
      section="Legal"
      title="Terms of service"
      reviewed="2026-08-18"
      counselCleared={false}
      intro={
        <>
          What Dedupr does, what we commit to, and what you agree to by using it. Commercial and
          liability terms are marked where they are still with counsel — we would rather show you a
          gap than a plausible clause nobody decided.
        </>
      }
    >
      <p>
        <strong>Dedupr is pre-launch and has no customers.</strong> Nothing here describes a running
        service with users on it.
      </p>

      <h2 id="what">1 · What Dedupr is</h2>
      <p>
        Dedupr connects the systems you already use — a CRM, a marketing or sales tool, a document
        store, or a file export — profiles what is in them, finds duplicates and data-quality
        problems, and, <strong>only after one of your people approves each change</strong>, writes
        corrections back or exports cleaned copies to storage you own.
      </p>

      <h2 id="account">2 · Your account</h2>
      <p>
        You need an account to use Dedupr. You are responsible for who you invite and what role you
        give them. Sign-in is by emailed magic link, so there is no password to lose.{' '}
        <strong>We offer no MFA today</strong> — said plainly, because a security expectation you
        assume and we do not provide is worse than one we decline.
      </p>
      <p>
        You must be authorised to connect any system you connect, and to permit the processing set out
        in <a href="#/dpa">the DPA</a>.
      </p>

      <h2 id="commitments">3 · What we will not do to your data</h2>
      <p>These are commitments, not descriptions of current behaviour:</p>
      <ul>
        <li>
          <strong>Read before write.</strong> Every connection is made with read-only access.
          Permission to change anything is requested separately, per action type, the first time one
          of your people approves an action of that type.
        </li>
        <li>
          <strong>No change without a person approving it.</strong> Every mutating action is
          snapshotted first and reversible for thirty days — except permanent deletion, which is off
          unless you turn it on and which says in the confirmation that it cannot be undone.
        </li>
        <li><strong>We never sell your data and never train models on it.</strong></li>
        <li>
          <strong>Your records stay in the region you chose</strong>, with two exceptions we publish
          rather than hide: transactional email, and the vector index behind AI-ready surfaces.
        </li>
      </ul>

      <h2 id="acceptable">4 · What you must not do</h2>
      <p>
        Use Dedupr to process data you have no right to process; attempt to reach another customer’s
        data; probe or load-test the service without asking us first; or resell access without a
        written agreement.
      </p>

      <h2 id="plans">5 · Plans, payment and change</h2>
      <p>
        Plans, allowances and prices are on the <a href="#/pricing">pricing page</a>. Two behaviours
        belong in the terms rather than only in the interface:
      </p>
      <ul>
        <li>
          <strong>Going over an allowance does not stop your syncs.</strong> Dedupr sits in your data
          flow, so a hard stop would be an outage rather than a billing event. We warn, and where an
          overage rate exists we bill it.
        </li>
        <li>
          <strong>A failed payment does not delete anything.</strong> After a grace period the
          workspace becomes read-only; findings and exports stay readable, and paying reactivates it
          immediately.
        </li>
      </ul>
      <p className="land-legal__pending">
        <strong>With counsel:</strong> notice period for price changes, refund position, minimum term
        and auto-renewal.
      </p>

      <h2 id="availability">6 · Availability</h2>
      <p>
        <strong>We make no uptime commitment, and we will not imply one.</strong> There is no SLA, no
        service credits, and no published availability target we are willing to be held to yet. When
        that changes it will be a written commitment on this page with a number in it.
      </p>

      <h2 id="ending">7 · Ending it</h2>
      <p>
        You may close your workspace at any time from Settings. Closure is staged deliberately: the
        workspace stops taking new work, you are offered an export, you must acknowledge that any live
        rollback window will be voided, and after a <strong>30-day grace period</strong> the data is
        purged and a signed attestation of the purge is retained. ⚠️ The audit trail survives on its
        own two-year clock and holds no field values — it is the remaining evidence that the deletion
        happened.
      </p>
      <p>
        We may suspend an account for non-payment (§5) or for a breach of §4.
      </p>
      <p className="land-legal__pending">
        <strong>With counsel:</strong> notice and cure period before suspension or termination for
        cause.
      </p>

      <h2 id="liability">8 · Liability, warranties and governing law</h2>
      <p className="land-legal__pending">
        <strong>Not drafted here, deliberately.</strong> Limitation of liability, warranty
        disclaimers, indemnities, governing law and jurisdiction are commercial and legal positions.
        Writing plausible ones would be worse than leaving them open, because a reader cannot tell an
        invented clause from a considered one.
      </p>
      <p>Two things we will say now:</p>
      <ul>
        <li>
          Dedupr’s findings are <strong>proposals for a person to approve</strong>, not automated
          decisions.
        </li>
        <li>
          Every applied change is <strong>snapshotted and reversible for thirty days</strong>, which is
          the practical protection behind everything above.
        </li>
      </ul>

      <h2 id="changes">9 · Changes to these terms</h2>
      <p>
        We will post a new version here with a new date and tell account owners by email where a
        change is material. This is v0.1 and <strong>has not been through legal review</strong>.
      </p>
      <p>
        <button type="button" className="land-btn land-btn--plain land-legal__inline-link" onClick={onGoLegal}>
          Read the Data Processing Agreement →
        </button>
      </p>
    </PolicyPage>
  );
}
